import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchMerchantList,
  fetchMerchantDetail,
  createMerchant,
  updateMerchant,
  deleteMerchant,
} from '@/services/merchantService';
import type { Merchant, MerchantFormValues } from '@/types/merchant';

interface MerchantState {
  items: Merchant[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
}

const initialState: MerchantState = {
  items: [],
  total: 0,
  page: 1,
  pageSize: 10,
  loading: false,
  error: null,
};

// 获取商户列表
export const getMerchantList = createAsyncThunk(
  'merchant/getList',
  async (params: { page?: number; pageSize?: number }) => {
    // 这里假设 fetchMerchantList 返回 { items, total }
    const response = await fetchMerchantList(params);
    return response;
  }
);

// 获取商户详情
export const getMerchantDetail = createAsyncThunk(
  'merchant/getDetail',
  async (id: number) => {
    const response = await fetchMerchantDetail(id);
    return response;
  }
);

// 创建商户
export const createMerchantAsync = createAsyncThunk(
  'merchant/create',
  async (data: MerchantFormValues) => {
    const response = await createMerchant(data);
    return response;
  }
);

// 更新商户
export const updateMerchantAsync = createAsyncThunk(
  'merchant/update',
  async ({ id, data }: { id: number; data: MerchantFormValues }) => {
    const response = await updateMerchant(id, data);
    return response;
  }
);

// 删除商户
export const deleteMerchantAsync = createAsyncThunk(
  'merchant/delete',
  async (id: number) => {
    await deleteMerchant(id);
    return id;
  }
);

const merchantSlice = createSlice({
  name: 'merchant',
  initialState,
  reducers: {
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // 获取商户列表
      .addCase(getMerchantList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMerchantList.fulfilled, (state, action) => {
        state.loading = false;
        // 兼容数组和分页对象
        if (Array.isArray(action.payload)) {
          state.items = action.payload;
          state.total = action.payload.length;
        } else {
          const payload = action.payload as { items: Merchant[]; total: number };
          state.items = payload.items;
          state.total = payload.total;
        }
      })
      .addCase(getMerchantList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取商户列表失败';
      })
      // 创建商户
      .addCase(createMerchantAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMerchantAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
        state.total += 1;
      })
      .addCase(createMerchantAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '创建商户失败';
      })
      // 更新商户
      .addCase(updateMerchantAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMerchantAsync.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateMerchantAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '更新商户失败';
      })
      // 删除商户
      .addCase(deleteMerchantAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMerchantAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter((item) => item.id !== action.payload);
        state.total -= 1;
      })
      .addCase(deleteMerchantAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '删除商户失败';
      });
  },
});

export const { setPage, setPageSize, clearError } = merchantSlice.actions;
export default merchantSlice.reducer; 